/**
 * File Metadata Edit Modal Component
 *
 * Modal dialog for editing file description and status
 * Used in file repository to update metadata for uploaded files
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import { FileStatus, FILE_STATUS_LABELS } from '../../models/FileStorage';

interface FileMetadataEditModalProps {
  open: boolean;
  filename: string;
  currentDescription?: string;
  currentStatus?: FileStatus;
  onClose: () => void;
  onSave: (description: string, status: FileStatus) => void;
}

/**
 * FileMetadataEditModal Component
 *
 * Provides a form to edit file metadata:
 * - Description: Free text field (max 1000 characters)
 * - Status: Select dropdown with draft/production/deprecated options
 */
export const FileMetadataEditModal: React.FC<FileMetadataEditModalProps> = ({
  open,
  filename,
  currentDescription = '',
  currentStatus = 'draft',
  onClose,
  onSave,
}) => {
  const [description, setDescription] = useState(currentDescription);
  const [status, setStatus] = useState<FileStatus>(currentStatus);

  // Update local state when props change
  useEffect(() => {
    setDescription(currentDescription || '');
    setStatus(currentStatus || 'draft');
  }, [currentDescription, currentStatus, open]);

  const handleSave = () => {
    onSave(description, status);
  };

  const handleClose = () => {
    // Reset to current values when closing without saving
    setDescription(currentDescription || '');
    setStatus(currentStatus || 'draft');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        파일 메타데이터 편집
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            파일: {filename}
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="status-select-label">상태</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={status}
              label="상태"
              onChange={(e) => setStatus(e.target.value as FileStatus)}
            >
              <MenuItem value="draft">{FILE_STATUS_LABELS.draft}</MenuItem>
              <MenuItem value="production">{FILE_STATUS_LABELS.production}</MenuItem>
              <MenuItem value="deprecated">{FILE_STATUS_LABELS.deprecated}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="비고"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="파일에 대한 설명을 입력하세요"
            inputProps={{ maxLength: 1000 }}
            helperText={`${description.length}/1000`}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          취소
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};
